import { approxYear } from '../../domain/dates';
import { displayName } from '../../domain/names';
import { isEditor } from '../../domain/permissions';
import { buildGraph, validateNewRelationship } from '../../domain/relationships';
import type { ChangeRequest, CommandResult, Invitation, Relationship, UUID } from '../../domain/types';
import { daysFromNowISO, nowISO } from '../../lib/time';
import { uuid } from '../../lib/uuid';

/** Prototype invitation code — readable, and never a secret worth hashing here. */
function inviteToken(): string {
  const alphabet = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';
  let s = '';
  for (let i = 0; i < 6; i++) s += alphabet[Math.floor(Math.random() * alphabet.length)];
  return `INV-${s}`;
}
import type { AddRelativeCommand, AddRelativeResult, RelationshipProposal, RelationshipRepository } from '../types';
import { actorOf, denied, guarded, invalid, notFound, okResult } from './actor';
import { MockDb } from './MockDb';
import { MockPersonRepository } from './MockPersonRepository';

export class MockRelationshipRepository implements RelationshipRepository {
  constructor(private db: MockDb, private people: MockPersonRepository) {}

  async list(): Promise<Relationship[]> {
    await this.db.read();
    return [...this.db.t.relationships];
  }

  private graph() {
    return buildGraph(this.db.t.persons.filter((p) => !p.archived && !p.mergedIntoId).map((p) => p.id), this.db.t.relationships, ['approved', 'pending']);
  }

  private notifyEditors(exceptAccountId: UUID, title: string, titleHe: string): void {
    for (const m of this.db.t.family_memberships) {
      if (m.status === 'active' && (m.role === 'editor' || m.role === 'admin') && m.accountId !== exceptAccountId) {
        this.db.notify({ accountId: m.accountId, kind: 'review', title, titleHe, link: '/manage/review' });
      }
    }
  }

  async addRelative(cmd: AddRelativeCommand): Promise<CommandResult<AddRelativeResult>> {
    return guarded(this.db, async () => {
      const db = this.db;
      const actor = actorOf(db);
      const idem = db.idem(cmd.idempotencyKey);
      if (idem) {
        const p = await this.people.get(idem.entityId);
        return p ? okResult({ person: p, relationships: db.t.relationships.filter((r) => r.fromPersonId === p.id || r.toPersonId === p.id) }, { noop: true }) : notFound();
      }
      const anchor = db.personWithNames(cmd.anchorPersonId);
      if (!anchor) return notFound();

      // Resolve or create the relative
      let personId: UUID;
      let createdNew = false;
      if (cmd.existingPersonId) {
        if (!db.t.persons.some((p) => p.id === cmd.existingPersonId && !p.archived)) return notFound();
        personId = cmd.existingPersonId;
      } else if (cmd.newPerson) {
        const dups = await this.people.findLikelyDuplicates({ names: cmd.newPerson.names, birthYear: approxYear(cmd.newPerson.birth) ?? undefined, connectedToPersonId: cmd.anchorPersonId });
        const strong = dups.find((d) => d.confidence >= 0.8);
        if (strong && !cmd.ignoreDuplicateWarning) {
          return invalid('rel.error.duplicate', { name: displayName(strong.person, 'en'), personId: strong.person.id });
        }
        const now = nowISO();
        personId = uuid();
        db.t.persons.push({
          id: personId, familyId: actor.session.familyId, status: cmd.newPerson.status, gender: cmd.newPerson.gender, birth: cmd.newPerson.birth, death: cmd.newPerson.death,
          // Someone the family has not met yet: their circle starts dimmed and
          // becomes solid once enough relatives recognise them (or an
          // administrator says so outright). Adding them as an editor or an
          // administrator is itself that approval.
          birthPlaceId: cmd.newPerson.birthPlaceId, portraitUrl: cmd.newPerson.portraitUrl, portraitHue: Math.floor(Math.random() * 16), reviewStatus: isEditor(actor.session.role) ? 'approved' : 'pending', lockedFields: [],
          archived: false, notes: cmd.newPerson.notes, version: 1, createdAt: now, updatedAt: now, updatedBy: actor.session.accountId,
        });
        for (const n of cmd.newPerson.names) db.t.person_names.push({ ...n, id: uuid(), personId });
        createdNew = true;
        if (strong) {
          db.t.duplicate_suggestions.push({ id: uuid(), familyId: actor.session.familyId, personAId: strong.person.id, personBId: personId, confidence: strong.confidence, reason: 'Added despite a duplicate warning', status: 'open', createdAt: now });
        }
      } else {
        return invalid('rel.error.noPerson');
      }

      // Build the structural proposals
      const proposals: RelationshipProposal[] = [];
      const parentSub = cmd.parentSubtype ?? 'unspecified';
      switch (cmd.relation) {
        case 'parent':
          proposals.push({ type: 'parent_child', fromPersonId: personId, toPersonId: anchor.id, subtype: parentSub, note: cmd.note });
          break;
        case 'child':
          proposals.push({ type: 'parent_child', fromPersonId: anchor.id, toPersonId: personId, subtype: parentSub, note: cmd.note });
          for (const co of cmd.sharedParentIds ?? []) if (co !== anchor.id) proposals.push({ type: 'parent_child', fromPersonId: co, toPersonId: personId, subtype: parentSub });
          break;
        case 'sibling': {
          const g = this.graph();
          const parentIds = cmd.sharedParentIds ?? (g.parentsOf.get(anchor.id) ?? []);
          if (!parentIds.length) return invalid('rel.error.siblingNeedsParent');
          for (const p of parentIds) proposals.push({ type: 'parent_child', fromPersonId: p, toPersonId: personId, subtype: parentSub });
          break;
        }
        case 'partner': {
          const [a, b] = anchor.id < personId ? [anchor.id, personId] : [personId, anchor.id];
          proposals.push({ type: 'partner', fromPersonId: a, toPersonId: b, subtype: cmd.partnerSubtype ?? 'unspecified', current: cmd.partnerCurrent ?? true, startDate: cmd.startDate, endDate: cmd.endDate, note: cmd.note });
          break;
        }
      }

      // Validate every link against the graph as it will be after previous links
      const g = this.graph();
      const relationships: Relationship[] = [];
      const now = nowISO();
      const direct = isEditor(actor.session.role);
      for (const p of proposals) {
        const v = validateNewRelationship(g, p);
        if (!v.ok) {
          if (v.messageKey === 'rel.error.exists') continue; // acknowledged no-op (section 16.3)
          if (createdNew) {
            db.t.persons = db.t.persons.filter((x) => x.id !== personId);
            db.t.person_names = db.t.person_names.filter((x) => x.personId !== personId);
          }
          return invalid(v.messageKey);
        }
        const rel: Relationship = {
          id: uuid(), familyId: actor.session.familyId, type: p.type, fromPersonId: p.fromPersonId, toPersonId: p.toPersonId, subtype: p.subtype, current: p.current,
          startDate: p.startDate, endDate: p.endDate, status: direct ? 'approved' : 'pending', note: p.note, locked: false, version: 1, createdAt: now, updatedAt: now, updatedBy: actor.session.accountId,
        };
        relationships.push(rel);
        db.t.relationships.push(rel);
        if (p.type === 'parent_child') {
          g.parentsOf.set(p.toPersonId, [...(g.parentsOf.get(p.toPersonId) ?? []), p.fromPersonId]);
          g.childrenOf.set(p.fromPersonId, [...(g.childrenOf.get(p.fromPersonId) ?? []), p.toPersonId]);
        }
      }

      let changeRequest: ChangeRequest | undefined;
      const personName = displayName(db.personWithNames(personId)!, 'en');
      const anchorName = displayName(anchor, 'en');
      const desc = `Add ${personName} as ${cmd.relation} of ${anchorName}`;
      if (!direct && relationships.length) {
        changeRequest = {
          id: uuid(), familyId: actor.session.familyId, kind: 'relationship_add',
          payload: { relationshipIds: relationships.map((r) => r.id), personId, anchorPersonId: anchor.id, relation: cmd.relation, createdNew },
          summary: desc, requesterAccountId: actor.session.accountId, reason: cmd.note, status: 'pending', targetEntityType: 'relationship', targetEntityId: relationships[0].id, createdAt: now, updatedAt: now,
        };
        db.t.change_requests.push(changeRequest);
        this.notifyEditors(actor.session.accountId, `${db.accountName(actor.session.accountId)}: ${desc}`, `${db.accountName(actor.session.accountId)}: ${desc}`);
      }
      if (createdNew) db.audit({ actorAccountId: actor.session.accountId, entityType: 'person', entityId: personId, action: 'create', description: `Person added as ${cmd.relation} of ${anchorName}`, entityVersion: 1 });
      for (const r of relationships) db.audit({ actorAccountId: actor.session.accountId, entityType: 'relationship', entityId: r.id, action: 'create', description: desc, entityVersion: 1, approvalStatus: direct ? 'approved' : 'pending' });

      // Whoever added them counts as the first person recognising them.
      if (createdNew && !db.t.person_approvals.some((a) => a.personId === personId && a.accountId === actor.session.accountId)) {
        db.t.person_approvals.push({ id: uuid(), familyId: actor.session.familyId, personId, accountId: actor.session.accountId, byAdmin: direct, createdAt: now });
      }

      // …and becomes the person who looks after the new circle, so nobody else
      // can change its picture or details (domain/ownership.ts). An
      // administrator can hand it to someone else later.
      if (createdNew) {
        db.t.profile_management_grants.push({
          id: uuid(), familyId: actor.session.familyId, helperAccountId: actor.session.accountId, managedPersonId: personId,
          scope: 'full', grantedByAccountId: actor.session.accountId, status: 'active', createdAt: now,
        });
        db.audit({ actorAccountId: actor.session.accountId, entityType: 'grant', entityId: personId, action: 'grant', description: `${db.accountName(actor.session.accountId)} looks after ${personName}`, });
      }

      // An email turns "added to the tree" and "invited to join" into one step.
      let invitation: Invitation | undefined;
      const inviteEmail = cmd.inviteEmail?.trim();
      if (createdNew && inviteEmail) {
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(inviteEmail)) return invalid('add.invite.badEmail');
        invitation = {
          id: uuid(), familyId: actor.session.familyId, token: inviteToken(), intendedPersonId: personId, destination: inviteEmail,
          expiresAt: daysFromNowISO(14), status: 'pending', createdByAccountId: actor.session.accountId, createdAt: now,
        };
        db.t.invitations.push(invitation);
        db.audit({ actorAccountId: actor.session.accountId, entityType: 'access', entityId: invitation.id, action: 'create', description: `Invitation sent to ${inviteEmail} for ${personName}` });
      }

      const approvals = db.t.person_approvals.filter((a) => a.personId === personId).length;
      const person = (await this.people.get(personId))!;
      const approvalsNeeded = person.reviewStatus === 'approved' ? 0 : Math.max(0, db.governance().memberApprovalsRequired - approvals);

      db.rememberIdem(cmd.idempotencyKey, 'person', personId);
      await db.commit(['persons', 'person_names', 'relationships', 'change_requests', 'audit_events', 'notifications', 'idempotency', 'duplicate_suggestions', 'person_approvals', 'profile_management_grants', 'invitations'], [personId, anchor.id]);
      return okResult({ person, relationships, changeRequest, invitation, approvalsNeeded });
    });
  }

  async propose(proposal: RelationshipProposal, idempotencyKey: string, reason?: string): Promise<CommandResult<{ relationship?: Relationship; changeRequest?: ChangeRequest }>> {
    return guarded(this.db, async () => {
      const db = this.db;
      const actor = actorOf(db);
      const idem = db.idem(idempotencyKey);
      if (idem) return okResult({ relationship: db.t.relationships.find((r) => r.id === idem.entityId) }, { noop: true });
      const g = this.graph();
      const norm = proposal.type === 'partner' && proposal.toPersonId < proposal.fromPersonId ? { ...proposal, fromPersonId: proposal.toPersonId, toPersonId: proposal.fromPersonId } : proposal;
      const v = validateNewRelationship(g, norm);
      if (!v.ok) {
        if (v.messageKey === 'rel.error.exists') return okResult({ relationship: undefined }, { noop: true });
        return invalid(v.messageKey);
      }
      const now = nowISO();
      const direct = isEditor(actor.session.role);
      const rel: Relationship = {
        id: uuid(), familyId: actor.session.familyId, type: norm.type, fromPersonId: norm.fromPersonId, toPersonId: norm.toPersonId, subtype: norm.subtype, current: norm.current,
        startDate: norm.startDate, endDate: norm.endDate, status: direct ? 'approved' : 'pending', note: norm.note, locked: false, version: 1, createdAt: now, updatedAt: now, updatedBy: actor.session.accountId,
      };
      db.t.relationships.push(rel);
      const from = db.personWithNames(rel.fromPersonId);
      const to = db.personWithNames(rel.toPersonId);
      const desc = rel.type === 'parent_child' ? `${from ? displayName(from, 'en') : '?'} is a parent of ${to ? displayName(to, 'en') : '?'}` : `${from ? displayName(from, 'en') : '?'} and ${to ? displayName(to, 'en') : '?'} are partners`;
      let changeRequest: ChangeRequest | undefined;
      if (!direct) {
        changeRequest = { id: uuid(), familyId: actor.session.familyId, kind: 'relationship_add', payload: { relationshipIds: [rel.id] }, summary: desc, requesterAccountId: actor.session.accountId, reason, status: 'pending', targetEntityType: 'relationship', targetEntityId: rel.id, createdAt: now, updatedAt: now };
        db.t.change_requests.push(changeRequest);
        this.notifyEditors(actor.session.accountId, `${db.accountName(actor.session.accountId)}: ${desc}`, `${db.accountName(actor.session.accountId)}: ${desc}`);
      }
      db.audit({ actorAccountId: actor.session.accountId, entityType: 'relationship', entityId: rel.id, action: 'create', description: desc, entityVersion: 1, approvalStatus: direct ? 'approved' : 'pending' });
      db.rememberIdem(idempotencyKey, 'relationship', rel.id);
      await db.commit(['relationships', 'change_requests', 'audit_events', 'notifications', 'idempotency'], [rel.fromPersonId, rel.toPersonId]);
      return okResult({ relationship: rel, changeRequest });
    });
  }

  async update(id: UUID, expectedVersion: number, patch: Partial<Pick<Relationship, 'subtype' | 'current' | 'startDate' | 'endDate' | 'note'>>, idempotencyKey: string): Promise<CommandResult<Relationship>> {
    return guarded(this.db, async () => {
      const actor = actorOf(this.db);
      const rel = this.db.t.relationships.find((r) => r.id === id);
      if (!rel) return notFound();
      if (this.db.idem(idempotencyKey)) return okResult(rel, { noop: true });
      if (!isEditor(actor.session.role)) return denied('rel.error.editorOnly');
      if (rel.locked && actor.session.role !== 'admin') return { ok: false, kind: 'locked', messageKey: 'rel.locked' };
      if (rel.version !== expectedVersion) {
        return { ok: false, kind: 'conflict', conflicts: [{ field: 'relationship', currentValue: rel, yourValue: patch, baseValue: null, changedByAccountId: rel.updatedBy, changedAt: rel.updatedAt }], current: rel, currentVersion: rel.version };
      }
      const before = { ...rel };
      Object.assign(rel, patch);
      rel.version += 1;
      rel.updatedAt = nowISO();
      rel.updatedBy = actor.session.accountId;
      this.db.audit({ actorAccountId: actor.session.accountId, entityType: 'relationship', entityId: rel.id, action: 'update', before: { subtype: before.subtype, current: before.current }, after: { subtype: rel.subtype, current: rel.current }, description: 'Relationship details updated', entityVersion: rel.version });
      this.db.rememberIdem(idempotencyKey, 'relationship', rel.id);
      await this.db.commit(['relationships', 'audit_events', 'idempotency'], [rel.fromPersonId, rel.toPersonId]);
      return okResult(rel);
    });
  }

  async remove(id: UUID, expectedVersion: number, idempotencyKey: string, reason?: string): Promise<CommandResult<{ removed: true } | { changeRequest: ChangeRequest }>> {
    type R = { removed: true } | { changeRequest: ChangeRequest };
    return guarded<R>(this.db, async () => {
      const actor = actorOf(this.db);
      const rel = this.db.t.relationships.find((r) => r.id === id);
      if (!rel) return okResult<R>({ removed: true as const }, { noop: true });
      if (rel.locked && actor.session.role !== 'admin') return { ok: false, kind: 'locked', messageKey: 'rel.locked' };
      const now = nowISO();
      if (!isEditor(actor.session.role)) {
        const cr: ChangeRequest = { id: uuid(), familyId: actor.session.familyId, kind: 'relationship_remove', payload: { relationshipId: id }, summary: 'Remove a family connection', requesterAccountId: actor.session.accountId, reason, status: 'pending', targetEntityType: 'relationship', targetEntityId: id, createdAt: now, updatedAt: now };
        this.db.t.change_requests.push(cr);
        rel.status = 'disputed';
        this.notifyEditors(actor.session.accountId, `${this.db.accountName(actor.session.accountId)} asked to remove a family connection`, `${this.db.accountName(actor.session.accountId)} ביקש/ה להסיר קשר משפחתי`);
        await this.db.commit(['relationships', 'change_requests', 'notifications'], [rel.fromPersonId, rel.toPersonId]);
        return okResult<R>({ changeRequest: cr });
      }
      if (rel.version !== expectedVersion) {
        return { ok: false, kind: 'conflict', conflicts: [{ field: 'relationship', currentValue: rel, yourValue: null, baseValue: null, changedByAccountId: rel.updatedBy, changedAt: rel.updatedAt }], current: { removed: true as const }, currentVersion: rel.version };
      }
      this.db.t.relationships = this.db.t.relationships.filter((r) => r.id !== id);
      this.db.audit({ actorAccountId: actor.session.accountId, entityType: 'relationship', entityId: id, action: 'archive', before: { relationship: rel }, description: `Removed connection (${rel.type})`, entityVersion: rel.version + 1 });
      this.db.rememberIdem(idempotencyKey, 'relationship', id);
      await this.db.commit(['relationships', 'audit_events', 'idempotency'], [rel.fromPersonId, rel.toPersonId]);
      return okResult<R>({ removed: true as const });
    });
  }

  async lock(id: UUID, locked: boolean): Promise<CommandResult<Relationship>> {
    return guarded(this.db, async () => {
      const actor = actorOf(this.db);
      if (actor.session.role !== 'admin') return denied();
      const rel = this.db.t.relationships.find((r) => r.id === id);
      if (!rel) return notFound();
      rel.locked = locked;
      this.db.audit({ actorAccountId: actor.session.accountId, entityType: 'relationship', entityId: rel.id, action: locked ? 'lock' : 'unlock', description: locked ? 'Relationship locked' : 'Relationship unlocked' });
      await this.db.commit(['relationships', 'audit_events']);
      return okResult(rel);
    });
  }
}
